import { Accordion, AccordionDetails, AccordionSummary, Box, Divider, Paper, Typography, makeStyles } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import dayjs from "dayjs";
import React from "react";

import theme from "@/theme";

type MonthlyBalanceSheetLike = {
  // view_models 側の result は index signature を持たない型のため、ここでは unknown として受け取って適宜キャストする
  result?: unknown;
  property_acquisition_tax?: number;
  price_of_misc_expences_at_bought?: number;
};

type Props = {
  monthlyResults: MonthlyBalanceSheetLike[];
};

const useStyles = makeStyles(() => ({
  card: {
    borderRadius: 16,
    border: `1px solid ${theme.palette.semantic["border/default"]}`,
    overflow: "hidden",
  },
  cardHeader: {
    backgroundColor: theme.palette.semantic["surface/card"],
    padding: theme.spacing(1.5, 2),
    textAlign: "left",
  },
  cardTitleRow: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  cardTitleRowCenter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  cardTitleGroup: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 1,
  },
  cardTotal: {
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 1,
    whiteSpace: "nowrap",
  },
  cardBody: {
    backgroundColor: theme.palette.common.white,
    padding: 0,
  },
  item: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  itemHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    // アコーディオン展開アイコン分の余白を確保して、アコーディオン有無で金額の位置がズレないようにする
    paddingRight: theme.spacing(5),
  },
  itemName: {
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1,
  },
  itemAmount: {
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1,
  },
  itemMeta: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1,
    gap: theme.spacing(1),
  },
  itemNameMetaGroup: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  bodyText: {
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 1,
  },
  labelRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  monoAmount: {
    whiteSpace: "nowrap",
  },
  accordion: {
    boxShadow: "none",
    "&:before": {
      display: "none",
    },
    "&.Mui-expanded": {
      margin: 0,
    },
  },
  accordionSummary: {
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(2),
    minHeight: 48,
    "&.Mui-expanded": {
      minHeight: 48,
    },
  },
  accordionSummaryContent: {
    margin: 0,
    "&.Mui-expanded": {
      margin: 0,
    },
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  accordionSummaryLeft: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  accordionDetails: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.semantic["surface/subtle"],
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
  },
  detailLabel: {
    color: theme.palette.text.primary,
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 1,
  },
  detailValue: {
    color: theme.palette.text.primary,
    fontWeight: 400,
    fontSize: 14,
    lineHeight: 1,
    whiteSpace: "nowrap",
  },
}));

const toNumber = (v: unknown) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    const trimmed = v.trim();
    if (!trimmed) return 0;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const formatYenTitle = (n: number) => `${Math.round(n).toLocaleString()}円`;

type DetailRow = { label: string; amount: number };

const formatMonthKey = (v: unknown) => {
  if (!v) return "-";
  // Worker 経由で Dayjs が plain object 化しているケースがあるため、常に dayjs(...) に通して format する
  const input =
    v instanceof Date || typeof v === "string" || typeof v === "number"
      ? v
      : typeof v === "object" && (v as any).$d
      ? (v as any).$d
      : v;
  const d = dayjs(input as any);
  return d.isValid() ? d.format("YYYY/MM") : "-";
};

const pickYearly = (monthlyResults: MonthlyBalanceSheetLike[]) => {
  // MonthlyCashflowResultsOnSelectedPeriodWithYearlySum の最後尾が年合計
  if (!monthlyResults || monthlyResults.length === 0) return null;
  return monthlyResults[monthlyResults.length - 1] ?? null;
};

/*
 * 減価償却関連（将来実装用）
 * NOTE: 現状は表示しないため、unused lint を避けるためにコメントアウトして保持する。
 */
// const getStructureUsefulYears = (structure: string) => {
//   switch (structure) {
//     case "SRC造(鉄骨鉄筋コンクリート造)":
//     case "RC造(鉄筋コンクリート造)":
//       return 47;
//     case "S造(重量鉄骨造)":
//       return 34;
//     case "木造":
//       return 22;
//     default:
//       return 0;
//   }
// };
//
// const calcBuildingPriceIncludingTax = (property: Property) => {
//   const buildingWithoutTax = toNumber(property.priceof_building);
//   const facilitiesWithoutTax = toNumber(property.priceof_facilities);
//   const totalTax = toNumber(property.priceof_tax);
//   if (buildingWithoutTax === 0 && facilitiesWithoutTax === 0) return 0;
//   return buildingWithoutTax + (totalTax / (buildingWithoutTax + facilitiesWithoutTax)) * buildingWithoutTax;
// };
//
// const calcFacilitiesPriceIncludingTax = (property: Property) => {
//   const buildingWithoutTax = toNumber(property.priceof_building);
//   const facilitiesWithoutTax = toNumber(property.priceof_facilities);
//   const totalTax = toNumber(property.priceof_tax);
//   if (facilitiesWithoutTax === 0) return 0;
//   if (buildingWithoutTax === 0 && facilitiesWithoutTax === 0) return 0;
//   return (
//     facilitiesWithoutTax + (totalTax / (buildingWithoutTax + facilitiesWithoutTax)) * facilitiesWithoutTax
//   );
// };
//
// const calcActualUsefulYears = (property: Property) => {
//   const structure = String(property.structure ?? "");
//   const originalBuildingYears = getStructureUsefulYears(structure);
//   const originalFacilitiesYears = 15;
//   const builtAt = property.built_at ? dayjs(property.built_at) : null;
//   const boughtAt = property.bought_at ? dayjs(property.bought_at) : null;
//   if (!builtAt || !builtAt.isValid() || !boughtAt || !boughtAt.isValid()) return { building: 0, facilities: 0 };
//   if (!originalBuildingYears) return { building: 0, facilities: 0 };
//
//   const buildingLapsed = Math.min(boughtAt.diff(builtAt, "year"), originalBuildingYears);
//   const buildingUnlapsed = originalBuildingYears - buildingLapsed;
//   const buildingActual = Math.max(Math.floor(buildingLapsed * 0.2 + buildingUnlapsed), 2);
//
//   const facilitiesLapsed = Math.min(boughtAt.diff(builtAt, "year"), originalFacilitiesYears);
//   const facilitiesUnlapsed = originalFacilitiesYears - facilitiesLapsed;
//   const facilitiesActual = Math.max(Math.floor(facilitiesLapsed * 0.2 + facilitiesUnlapsed), 2);
//
//   return { building: buildingActual, facilities: facilitiesActual };
// };

export const TaxReturnCards: React.FC<Props> = ({ monthlyResults }) => {
  const classes = useStyles();
  const yearly = pickYearly(monthlyResults);
  const result = (yearly?.result ?? {}) as Record<string, unknown>;
  const monthlyOnly = React.useMemo(
    () => (monthlyResults && monthlyResults.length >= 2 ? monthlyResults.slice(0, -1) : monthlyResults ?? []),
    [monthlyResults]
  );

  const rentIncome = toNumber(result.rent_income);
  // management_fee_income には「共益費・管理費収入」だけでなく「その他収入（駐車場など）」が含まれるケースがあるため分離する
  const managementFeeIncome = toNumber(result.management_fee_income);
  const otherIncome = toNumber(result.other_income);
  const managementFeeIncomeOnly = Math.max(0, managementFeeIncome - otherIncome);
  const keyMoneyAndRenewal = toNumber(result.key_money) + toNumber(result.security_deposit);
  const totalIncome = rentIncome + managementFeeIncomeOnly + keyMoneyAndRenewal + otherIncome;

  // 必要経費（減価償却費は③で別表示）
  const rentalManagementCost =
    toNumber(result.management_cost) +
    toNumber(result.outsourcing_fee_of_new_lease_contract) +
    toNumber(result.outsourcing_fee_of_updating_lease_contract) +
    // 建物管理費（※一棟）は賃貸管理費に含める
    toNumber(result.building_management_cost) +
    // 一棟の管理会社委託費（建物＋賃貸管理含む）は「賃貸管理費」に含める
    toNumber(result.building_management_company_commission);
  const repairs = toNumber(result.repair_fund_cost) + toNumber(result.large_scale_repair_fund_cost);
  const supplies = toNumber(result.supplies_cost);
  const communication = toNumber(result.communication_cost);
  // 一棟の共用部水道光熱費は他の行と同様に年次計算結果（購入月の日割り込み）を使い、物件マスタの月額×月数で再計算しない
  const utilities = toNumber(result.building_shared_utility_expenses);
  const insurance = toNumber(result.fire_insurance_distributed_price) + toNumber(result.earthquake_insurance_distributed_price);
  // 固定資産税・都市計画税は「支払月」の計上額をそのまま合計する
  const propertyTax = toNumber(result.property_tax);
  const interest = toNumber(result.loan_interest);
  const otherExpenses =
    toNumber(result.misc_cost) +
    toNumber(result.misc_onetime_cost) +
    // 一棟のその他経費は「その他経費（包括）」に含める
    toNumber(result.building_misc_cost) +
    toNumber(yearly?.property_acquisition_tax) +
    toNumber(yearly?.price_of_misc_expences_at_bought);

  const totalNecessaryExpenses =
    rentalManagementCost + repairs + supplies + communication + utilities + insurance + propertyTax + interest + otherExpenses;

  const rentalManagementCostDetails: DetailRow[] = React.useMemo(() => {
    const rows: DetailRow[] = [
      { label: "賃貸管理費", amount: toNumber(result.management_cost) },
      { label: "新規賃貸契約手数料", amount: toNumber(result.outsourcing_fee_of_new_lease_contract) },
      { label: "賃貸契約更新時手数料", amount: toNumber(result.outsourcing_fee_of_updating_lease_contract) },
      { label: "建物管理費", amount: toNumber(result.building_management_cost) },
      { label: "管理会社委託費（※一棟）", amount: toNumber(result.building_management_company_commission) },
    ];
    return rows.filter((r) => r.amount !== 0);
  }, [result]);

  const repairsDetails: DetailRow[] = React.useMemo(() => {
    const rows: DetailRow[] = [
      { label: "修繕積立金", amount: toNumber(result.repair_fund_cost) },
      { label: "設備修繕・大規模修繕費用", amount: toNumber(result.large_scale_repair_fund_cost) },
    ];
    return rows.filter((r) => r.amount !== 0);
  }, [result]);

  const insuranceDetails: DetailRow[] = React.useMemo(() => {
    const rows: DetailRow[] = [
      { label: "火災保険料", amount: toNumber(result.fire_insurance_distributed_price) },
      { label: "地震保険料", amount: toNumber(result.earthquake_insurance_distributed_price) },
    ];
    return rows.filter((r) => r.amount !== 0);
  }, [result]);

  const propertyTaxDetails: DetailRow[] = React.useMemo(() => {
    return monthlyOnly
      .map((m) => {
        const amount = toNumber((m as any)?.result?.property_tax);
        if (!amount) return null;
        const key = formatMonthKey((m as any)?.month);
        return { label: `${key}（支払月）`, amount };
      })
      .filter((v): v is DetailRow => Boolean(v));
  }, [monthlyOnly]);

  /*
   * 減価償却費（年合計）
   * NOTE: 建物・設備の種類によって変化し、現状は表示しないため計算は将来実装用に残す。
   */
  // const buildingDepreciation = toNumber(result.monthly_building_depreciation);
  // const facilitiesDepreciation = toNumber(result.monthly_facilities_depreciation);
  // const totalDepreciation = buildingDepreciation + facilitiesDepreciation;

  /*
   * 減価償却のメタ（取得価額・耐用年数）
   * NOTE: 将来、減価償却の表示を実装する場合に利用する。
   */
  // const buildingAcquisitionCost = selectedProperties.reduce(
  //   (sum, p) => sum + calcBuildingPriceIncludingTax(p.property),
  //   0
  // );
  // const facilitiesAcquisitionCost = selectedProperties.reduce(
  //   (sum, p) => sum + calcFacilitiesPriceIncludingTax(p.property),
  //   0
  // );
  // const usefulYears = selectedProperties.length === 1 ? calcActualUsefulYears(selectedProperties[0].property) : null;

  const realEstateIncomeWithoutDepreciation = totalIncome - totalNecessaryExpenses;

  const otherIncomeDetails = React.useMemo(() => {
    // 現状の「その他収入（駐車場など）」は other_income に集約されているため、項目別合計として1項目で表示する。
    const rows = [{ label: "その他収入（駐車場など）", amount: otherIncome }];
    return rows.filter((r) => r.amount !== 0);
  }, [otherIncome]);

  const otherExpensesDetails = React.useMemo(() => {
    const rows = [
      { label: "その他経費（毎月）", amount: toNumber(result.misc_cost) },
      { label: "その他一過性の支出", amount: toNumber(result.misc_onetime_cost) },
      { label: "その他経費（※一棟向け）", amount: toNumber(result.building_misc_cost) },
      { label: "物件取得税", amount: toNumber(yearly?.property_acquisition_tax) },
      { label: "購入時諸費用", amount: toNumber(yearly?.price_of_misc_expences_at_bought) },
    ];
    return rows.filter((r) => r.amount !== 0);
  }, [result, yearly]);

  return (
    <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
      <Paper className={classes.card}>
        <Box className={classes.cardHeader}>
          <Box className={classes.cardTitleRow}>
            <Typography className={classes.cardTitle}>
              【収入の部】
            </Typography>
            <Typography className={classes.cardTotal}>
              {formatYenTitle(totalIncome)}
            </Typography>
          </Box>
        </Box>
        <Divider />

        <Box className={classes.cardBody}>
          <Box className={classes.item}>
            <Box className={classes.itemHeader}>
              <Box className={classes.itemNameMetaGroup}>
                <Typography className={classes.itemName}>
                  家賃収入
                </Typography>
                <Typography className={classes.itemMeta}>
                  申告書：収入金額（賃貸料）
                </Typography>
              </Box>
              <Typography className={classes.itemAmount}>
                {formatYenTitle(rentIncome)}
              </Typography>
            </Box>
          </Box>
          <Divider />

          <Box className={classes.item}>
            <Box className={classes.itemHeader}>
              <Box className={classes.itemNameMetaGroup}>
                <Typography className={classes.itemName}>
                  共益費・管理費収入
                </Typography>
                <Typography className={classes.itemMeta}>
                  申告書：収入金額（賃貸料に含めて記載）
                </Typography>
              </Box>
              <Typography className={classes.itemAmount}>
                {formatYenTitle(managementFeeIncomeOnly)}
              </Typography>
            </Box>
          </Box>
          <Divider />

          <Box className={classes.item}>
            <Box className={classes.itemHeader}>
              <Box className={classes.itemNameMetaGroup}>
                <Typography className={classes.itemName}>
                  礼金・更新料
                </Typography>
                <Typography className={classes.itemMeta}>
                  申告書：その他の収入
                </Typography>
              </Box>
              <Typography className={classes.itemAmount}>
                {formatYenTitle(keyMoneyAndRenewal)}
              </Typography>
            </Box>
          </Box>
          <Divider />

          <Accordion className={classes.accordion}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              className={classes.accordionSummary}
              classes={{ content: classes.accordionSummaryContent }}
            >
              <Box className={classes.accordionSummaryLeft}>
                <Typography className={classes.itemName}>
                  その他収入（包括）
                </Typography>
                <Typography className={classes.itemMeta}>
                  申告書：その他の収入
                </Typography>
              </Box>
              <Typography className={classes.itemAmount}>
                {formatYenTitle(otherIncome)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              {otherIncomeDetails.length ? (
                otherIncomeDetails.map((row) => (
                  <Box key={row.label} className={classes.detailRow}>
                    <Typography className={classes.detailLabel}>
                      {row.label}
                    </Typography>
                    <Typography className={classes.detailValue}>
                      {formatYenTitle(row.amount)}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography className={classes.detailLabel}>
                  内訳がありません
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      </Paper>

      <Paper className={classes.card}>
        <Box className={classes.cardHeader}>
          <Box className={classes.cardTitleRow}>
            <Typography className={classes.cardTitle}>
              【必要経費の部】
            </Typography>
            <Typography className={classes.cardTotal}>
              {formatYenTitle(totalNecessaryExpenses)}
            </Typography>
          </Box>
        </Box>
        <Divider />

        <Box className={classes.cardBody}>
          {[
            { name: "賃貸管理費", amount: rentalManagementCost, form: "管理費", details: rentalManagementCostDetails },
            { name: "修繕費", amount: repairs, form: "修繕費", details: repairsDetails },
            { name: "消耗品費", amount: supplies, form: "消耗品費" },
            { name: "通信費", amount: communication, form: "通信費" },
            { name: "水道光熱費（※一棟）", amount: utilities, form: "水道光熱費" },
            { name: "火災・地震保険料", amount: insurance, form: "損害保険料", details: insuranceDetails },
            { name: "固定資産税・都市計画税", amount: propertyTax, form: "租税公課", details: propertyTaxDetails },
            { name: "借入金利子", amount: interest, form: "利子割引料" },
            { name: "その他経費（包括）", amount: otherExpenses, form: "その他の経費" },
          ].map((row: any, idx, arr) => (
            <React.Fragment key={row.name}>
              {row.name === "その他経費（包括）" ? (
                <Accordion className={classes.accordion}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className={classes.accordionSummary}
                    classes={{ content: classes.accordionSummaryContent }}
                  >
                    <Box className={classes.accordionSummaryLeft}>
                      <Typography className={classes.itemName}>
                        {row.name}
                      </Typography>
                      <Typography className={classes.itemMeta}>
                        申告書：{row.form}
                      </Typography>
                    </Box>
                    <Typography className={classes.itemAmount}>
                      {formatYenTitle(row.amount)}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    {otherExpensesDetails.length ? (
                      otherExpensesDetails.map((d) => (
                        <Box key={d.label} className={classes.detailRow}>
                          <Typography className={classes.detailLabel}>
                            {d.label}
                          </Typography>
                          <Typography className={classes.detailValue}>
                            {formatYenTitle(d.amount)}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography className={classes.detailLabel}>
                        内訳がありません
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              ) : row.details ? (
                <Accordion className={classes.accordion}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className={classes.accordionSummary}
                    classes={{ content: classes.accordionSummaryContent }}
                  >
                    <Box className={classes.accordionSummaryLeft}>
                      <Typography className={classes.itemName}>
                        {row.name}
                      </Typography>
                      <Typography className={classes.itemMeta}>
                        申告書：{row.form}
                      </Typography>
                    </Box>
                    <Typography className={classes.itemAmount}>
                      {formatYenTitle(row.amount)}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    {row.details.length ? (
                      row.details.map((d: DetailRow) => (
                        <Box key={d.label} className={classes.detailRow}>
                          <Typography className={classes.detailLabel}>
                            {d.label}
                          </Typography>
                          <Typography className={classes.detailValue}>
                            {formatYenTitle(d.amount)}
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography className={classes.detailLabel}>
                        内訳がありません
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              ) : (
                <Box className={classes.item}>
                  <Box className={classes.itemHeader}>
                    <Box className={classes.itemNameMetaGroup}>
                      <Typography className={classes.itemName}>
                        {row.name}
                      </Typography>
                      <Typography className={classes.itemMeta}>
                        申告書：{row.form}
                      </Typography>
                    </Box>
                    <Typography className={classes.itemAmount}>
                      {formatYenTitle(row.amount)}
                    </Typography>
                  </Box>
                </Box>
              )}
              {idx !== arr.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Box>
      </Paper>

      <Paper className={classes.card}>
        <Box className={classes.cardHeader}>
          <Box className={classes.cardTitleRowCenter}>
            <Box className={classes.cardTitleGroup}>
              <Typography className={classes.cardTitle}>【減価償却費】</Typography>
              <Typography className={classes.itemMeta}>
                ※減価償却は建物・設備の種類によって変化するため、表示しておりません
              </Typography>
            </Box>
            <Typography className={classes.cardTotal}>-</Typography>
          </Box>
        </Box>
        <Divider />

        <Box className={classes.cardBody}>
          <Box className={classes.item}>
            <Typography className={classes.itemName}>
              建物
            </Typography>
            <Box className={classes.labelRow}>
              <Typography className={classes.itemMeta}>取得価額</Typography>
              <Typography className={`${classes.itemMeta} ${classes.monoAmount}`}>-</Typography>
            </Box>
            <Box className={classes.labelRow}>
              <Typography className={classes.itemMeta}>耐用年数</Typography>
              <Typography className={`${classes.itemMeta} ${classes.monoAmount}`}>-</Typography>
            </Box>
            <Box className={classes.labelRow}>
              <Typography className={classes.itemMeta}>当年償却額</Typography>
              <Typography className={`${classes.itemMeta} ${classes.monoAmount}`}>-</Typography>
            </Box>
          </Box>
          <Divider />

          <Box className={classes.item}>
            <Typography className={classes.itemName}>
              設備
            </Typography>
            <Box className={classes.labelRow}>
              <Typography className={classes.itemMeta}>取得価額</Typography>
              <Typography className={`${classes.itemMeta} ${classes.monoAmount}`}>-</Typography>
            </Box>
            <Box className={classes.labelRow}>
              <Typography className={classes.itemMeta}>当年償却額</Typography>
              <Typography className={`${classes.itemMeta} ${classes.monoAmount}`}>-</Typography>
            </Box>
          </Box>
          <Divider />

          <Box className={classes.item}>
            <Box className={classes.itemHeader}>
              <Typography className={classes.itemName}>
                減価償却費合計
              </Typography>
              {/*
              <Typography className={classes.itemAmount}>
                -
              </Typography>
              */}
            </Box>
          </Box>
        </Box>
      </Paper>

      <Paper className={classes.card}>
        <Box className={classes.cardHeader}>
          <Box className={classes.cardTitleRow}>
            <Typography className={classes.cardTitle}>
              【不動産所得の金額】
            </Typography>
            <Typography className={classes.cardTotal}>
              {formatYenTitle(realEstateIncomeWithoutDepreciation)}
            </Typography>
          </Box>
        </Box>
        <Divider />

        <Box className={classes.cardBody}>
          <Box className={classes.item}>
            <Box className={classes.labelRow}>
              <Typography className={classes.bodyText}>収入合計</Typography>
              <Typography className={`${classes.bodyText} ${classes.monoAmount}`}>{formatYenTitle(totalIncome)}</Typography>
            </Box>
            <Box className={classes.labelRow}>
              <Typography className={classes.bodyText}>必要経費合計</Typography>
              <Typography className={`${classes.bodyText} ${classes.monoAmount}`}>
                {formatYenTitle(totalNecessaryExpenses)}
              </Typography>
            </Box>
            <Box className={classes.labelRow}>
              <Typography className={classes.bodyText}>減価償却費合計</Typography>
              <Typography className={`${classes.bodyText} ${classes.monoAmount}`}>-</Typography>
            </Box>
            <Divider style={{ margin: `${theme.spacing(1.5, -2)}` }} />
            <Box className={classes.labelRow}>
              <Typography className={classes.itemName}>
                不動産所得の金額 ※減価償却を除く
              </Typography>
              <Typography className={classes.itemAmount}>
                {formatYenTitle(realEstateIncomeWithoutDepreciation)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};
